import { useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from '@tanstack/react-query'
import {
  FileText,
  Sparkles,
  Shield,
  Menu,
  X,
  LogOut,
  LayoutDashboard,
  FolderOpen,
  CreditCard,
  Crown,
  Loader2,
  Check,
  Settings,
} from 'lucide-react'
import FeedbackButton from './FeedbackButton'
import ProjectSelector from './ProjectSelector'
import TrialBanner from './TrialBanner'
import { useUserRole } from '../../hooks/useUserRole'
import { subscriptionApi } from '../../services/api'
import { useGeneration } from '../../contexts/GenerationContext'

// User navigation - visible to all authenticated users
const userNavigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Projekty', href: '/projects', icon: FolderOpen },
  { name: 'Dokumenty', href: '/documents', icon: FileText },
  { name: 'Generuj Opinię', href: '/generate', icon: Sparkles },
  { name: 'Ustawienia', href: '/settings', icon: Settings },
]

// Admin navigation - visible only to administrators
const adminNavigation = [
  { name: 'Panel Admina', href: '/admin', icon: Shield },
]

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { logout } = useAuth0()
  const location = useLocation()
  const { isAdmin, user } = useUserRole()
  const { activeGeneration } = useGeneration()

  // Get subscription status to show upgrade button for trial users
  const { data: subscription } = useQuery({
    queryKey: ['subscription-current'],
    queryFn: () => subscriptionApi.getCurrent().then((r) => r.data),
    staleTime: 60000,
  })

  const isOnTrial = subscription?.status === 'Trial'
  const isActive = (href: string) => location.pathname === href

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b">
          <span className="text-xl font-bold text-blue-600">LexTool</span>
          <button onClick={() => setSidebarOpen(false)}>
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {userNavigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive(item.href)
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-300 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5 mr-3" />
              {item.name}
            </Link>
          ))}

          {/* Subscription section */}
          <div className="pt-4 mt-4 border-t border-gray-800">
            {isOnTrial ? (
              <a
                href="https://www.lextool.pl/cennik"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center mx-2 px-3 py-3 text-sm font-medium rounded-lg bg-gradient-to-r from-orange-500 to-yellow-500 text-white hover:from-orange-600 hover:to-yellow-600 transition-all"
              >
                <Crown className="w-5 h-5 mr-3" />
                <div>
                  <div>Przejdź na plan płatny</div>
                  {subscription?.daysUntilTrialEnds !== undefined && (
                    <div className="text-xs text-orange-100">
                      {subscription.daysUntilTrialEnds} dni próbnego
                    </div>
                  )}
                </div>
              </a>
            ) : (
              <a
                href="https://www.lextool.pl/cennik"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors text-gray-300 hover:bg-gray-800 hover:text-white"
              >
                <CreditCard className="w-5 h-5 mr-3" />
                Cennik
              </a>
            )}
          </div>

          {/* Admin section - only visible to administrators */}
          {isAdmin && (
            <div className="pt-4 mt-4 border-t border-gray-800">
              <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Administracja
              </p>
              {adminNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    location.pathname.startsWith('/admin')
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              ))}
            </div>
          )}
        </nav>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 min-h-0 bg-gray-900 border-r border-gray-800">
          {/* Logo */}
          <div className="flex items-center h-16 px-4 border-b border-gray-800">
            <span className="text-xl font-bold text-white">LexTool</span>
          </div>

          {/* Project Selector */}
          <div className="border-b border-gray-800">
            <ProjectSelector />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {userNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(item.href)
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5 mr-3" />
                {item.name}
              </Link>
            ))}

            {/* Subscription section */}
            <div className="pt-4 mt-4 border-t border-gray-800">
              {isOnTrial ? (
                // Trial user - show prominent upgrade button
                <a
                  href="https://www.lextool.pl/cennik"
                  className="flex items-center mx-2 px-3 py-3 text-sm font-medium rounded-lg bg-gradient-to-r from-orange-500 to-yellow-500 text-white hover:from-orange-600 hover:to-yellow-600 transition-all"
                >
                  <Crown className="w-5 h-5 mr-3" />
                  <div>
                    <div>Przejdz na plan platny</div>
                    {subscription?.daysUntilTrialEnds !== undefined && (
                      <div className="text-xs text-orange-100">
                        {subscription.daysUntilTrialEnds} dni probnego
                      </div>
                    )}
                  </div>
                </a>
              ) : (
                // Active user - show regular pricing link
                <a
                  href="https://www.lextool.pl/cennik"
                  className="flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors text-gray-300 hover:bg-gray-800 hover:text-white"
                >
                  <CreditCard className="w-5 h-5 mr-3" />
                  Cennik
                </a>
              )}
            </div>

            {/* Admin section - only visible to administrators */}
            {isAdmin && (
              <div className="pt-4 mt-4 border-t border-gray-800">
                <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Administracja
                </p>
                {adminNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname.startsWith('/admin')
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <item.icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </nav>

          {/* Background generation indicator */}
          {activeGeneration && (
            <div className="px-3 pb-3">
              <Link
                to={`/generate?sessionId=${activeGeneration.sessionId}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors bg-gray-800 hover:bg-gray-700"
              >
                {activeGeneration.status === 'generating' && (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                    <span className="text-blue-300 truncate">Generowanie...</span>
                  </>
                )}
                {activeGeneration.status === 'completed' && (
                  <>
                    <Check className="w-4 h-4 text-green-400" />
                    <span className="text-green-300 truncate">Opinia gotowa</span>
                  </>
                )}
                {activeGeneration.status === 'error' && (
                  <>
                    <X className="w-4 h-4 text-red-400" />
                    <span className="text-red-300 truncate">Blad generowania</span>
                  </>
                )}
              </Link>
            </div>
          )}

          {/* User info */}
          <div className="flex items-center p-4 border-t border-gray-800">
            <img
              src={user?.picture || '/default-avatar.png'}
              alt={user?.name || 'User'}
              className="w-8 h-8 rounded-full"
            />
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-200 truncate">
                {user?.name}
              </p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
            <button
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              className="p-2 text-gray-400 hover:text-white"
              title="Wyloguj"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Trial banner - shows when user is on trial */}
        <TrialBanner />

        {/* Top bar */}
        <div className="sticky top-0 z-10 flex items-center h-16 px-4 bg-white border-b lg:hidden">
          <button onClick={() => setSidebarOpen(true)} className="text-gray-500">
            <Menu className="w-6 h-6" />
          </button>
          <span className="ml-4 text-lg font-semibold text-blue-600">LexTool</span>
        </div>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>

      {/* Feedback button */}
      <FeedbackButton />
    </div>
  )
}
